import axios from 'axios';
import { V1API } from '../../../_helpers/APIPath';

const authConfig = (params) => ({
    headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
    },
    params
});

// Drop empty query values so "all" filters don't send `?verification_status=`
const cleanParams = (params = {}) => {
    const out = {};
    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
            out[key] = params[key];
        }
    });
    return out;
};

export default {
    namespaced: true,

    state: {
        professionals: [],
        professionalDetail: {},
        professionalLoading: false,
        professionalError: false,

        organisations: [],
        organisationDetail: {},
        organisationMembers: [],
        organisationLoading: false,
        organisationError: false
    },

    mutations: {
        SET_PROFESSIONALS(state, items) {
            state.professionals = items || [];
        },
        SET_PROFESSIONAL_DETAIL(state, detail) {
            state.professionalDetail = detail || {};
        },
        PROFESSIONAL_LOADING(state, loading) {
            state.professionalLoading = loading;
        },
        PROFESSIONAL_ERROR(state, hasError) {
            state.professionalError = hasError;
        },

        SET_ORGANISATIONS(state, items) {
            state.organisations = items || [];
        },
        SET_ORGANISATION_DETAIL(state, detail) {
            state.organisationDetail = detail || {};
        },
        SET_ORGANISATION_MEMBERS(state, items) {
            state.organisationMembers = items || [];
        },
        ORGANISATION_LOADING(state, loading) {
            state.organisationLoading = loading;
        },
        ORGANISATION_ERROR(state, hasError) {
            state.organisationError = hasError;
        }
    },

    actions: {
        // ---------- Professionals ----------
        async fetchProfessionals({ commit }, params = {}) {
            commit('PROFESSIONAL_LOADING', true);
            commit('PROFESSIONAL_ERROR', false);

            try {
                const result = await axios.get(V1API.skilled_professional_list, authConfig(cleanParams(params)));
                commit('SET_PROFESSIONALS', result.data.data);
            } catch (error) {
                commit('PROFESSIONAL_ERROR', true);
            } finally {
                commit('PROFESSIONAL_LOADING', false);
            }
        },

        async fetchProfessionalDetail({ commit }, id) {
            commit('PROFESSIONAL_LOADING', true);
            commit('PROFESSIONAL_ERROR', false);

            try {
                const result = await axios.get(V1API.skilled_professional_detail + id, authConfig());
                commit('SET_PROFESSIONAL_DETAIL', result.data.data);
            } catch (error) {
                commit('PROFESSIONAL_ERROR', true);
            } finally {
                commit('PROFESSIONAL_LOADING', false);
            }
        },

        // payload: { id, verification_status: 'verified' | 'rejected', review_note? }
        async updateVerification({ dispatch }, payload) {
            const body = { verification_status: payload.verification_status };
            if (payload.review_note) {
                body.review_note = payload.review_note;
            }

            try {
                const result = await axios.patch(V1API.skilled_professional_verification + payload.id, body, authConfig());
                const response = result.data;
                if (response.code == 200) {
                    dispatch('fetchProfessionalDetail', payload.id);
                }
                return response;
            } catch (error) {
                return { code: 500, response: 'Something went wrong. Please try again.' };
            }
        },

        // ---------- Organisations ----------
        async fetchOrganisations({ commit }, params = {}) {
            commit('ORGANISATION_LOADING', true);
            commit('ORGANISATION_ERROR', false);

            try {
                const result = await axios.get(V1API.sp_organisation_list, authConfig(cleanParams(params)));
                commit('SET_ORGANISATIONS', result.data.data);
            } catch (error) {
                commit('ORGANISATION_ERROR', true);
            } finally {
                commit('ORGANISATION_LOADING', false);
            }
        },

        async fetchOrganisationDetail({ commit }, id) {
            commit('ORGANISATION_LOADING', true);
            commit('ORGANISATION_ERROR', false);

            try {
                const result = await axios.get(V1API.sp_organisation_detail + id, authConfig());
                commit('SET_ORGANISATION_DETAIL', result.data.data);
            } catch (error) {
                commit('ORGANISATION_ERROR', true);
            } finally {
                commit('ORGANISATION_LOADING', false);
            }
        },

        // Professional profiles belonging to an organisation (gives profile ids for review links)
        async fetchOrganisationMembers({ commit }, organisationId) {
            try {
                const result = await axios.get(V1API.skilled_professional_list, authConfig({ organisation: organisationId }));
                commit('SET_ORGANISATION_MEMBERS', result.data.data);
            } catch (error) {
                commit('SET_ORGANISATION_MEMBERS', []);
            }
        },

        async toggleOrganisationVerification(context, id) {
            try {
                const result = await axios.patch(V1API.sp_organisation_toggle_verification + id, {}, authConfig());
                return result.data;
            } catch (error) {
                return { code: 500, response: 'Something went wrong. Please try again.' };
            }
        },

        // payload: { id, organisation: { ...fields } }
        async editOrganisation(context, payload) {
            try {
                const result = await axios.patch(V1API.sp_organisation_edit + payload.id, payload.organisation, authConfig());
                return result.data;
            } catch (error) {
                return { code: 500, response: 'Something went wrong. Please try again.' };
            }
        },

        async deleteOrganisation(context, id) {
            try {
                const result = await axios.delete(V1API.sp_organisation_delete + id, authConfig());
                return result.data;
            } catch (error) {
                return { code: 500, response: 'Something went wrong. Please try again.' };
            }
        }
    },

    getters: {}
};
